/**
 * Filters candidate files against a cumulative attachment size cap.
 *
 * Walks `candidateFiles` in order, accumulating a running total that starts
 * at `existingTotalBytes` (the size of files already attached). A candidate
 * is accepted only if adding it keeps the running total at or under
 * `capBytes` — a candidate that would push the total strictly over the cap
 * is rejected. Rejected candidates do not contribute to the running total,
 * so an earlier file in the same batch can still be accepted even if a
 * later one would overflow the cap.
 *
 * Shared by EmailComposePanel and EmailActionPreview, which both enforce the
 * same cumulative email attachment cap (see
 * apps/email/actions/send_email_action_handler.py::MAX_EMAIL_ATTACHMENT_TOTAL_BYTES).
 *
 * @param candidateFiles - Files to evaluate, in pick order.
 * @param existingTotalBytes - Sum of sizes of files already attached; seeds the running total.
 * @param capBytes - The cumulative cap in bytes.
 * @returns `accepted` — files that fit under the cap, in input order.
 *   `rejectedCount` — how many candidates were rejected for exceeding the cap.
 */
export function filterFilesByAttachmentCap(
  candidateFiles: File[],
  existingTotalBytes: number,
  capBytes: number
): { accepted: File[]; rejectedCount: number } {
  let runningTotal = existingTotalBytes;
  const accepted: File[] = [];
  let rejectedCount = 0;
  for (const file of candidateFiles) {
    if (runningTotal + file.size > capBytes) {
      rejectedCount += 1;
      continue;
    }
    runningTotal += file.size;
    accepted.push(file);
  }
  return { accepted, rejectedCount };
}
